import React, { useEffect, useMemo, useState } from "react";
import { personaApi } from "../api/routes";
import { Button, Card, Input, Pill, Select, TextArea, Toast } from "../components/ui/Controls";

const blankPersona = {
  personaId: "",
  version: "1",
  coachDisplayName: "",
  coachPersonality: "ENCOURAGING_SUPPORTIVE",
  imageUrl: "",
  basePersonaId: "core-chara",
  philosophyIdsByDistance: {},
  status: "ACTIVE",
  promptOverrides: {},
};

const distanceOptions = ["FIVE_K", "TEN_K", "HALF_MARATHON", "MARATHON", "ULTRA_MARATHON", "OFF_SEASON"];

const personalityOptions = [
  { label: "Calm & analytical", value: "CALM_ANALYTICAL" },
  { label: "Encouraging & supportive", value: "ENCOURAGING_SUPPORTIVE" },
  { label: "Direct performance", value: "DIRECT_PERFORMANCE" },
];

const statusOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

export default function PersonasPanel() {
  const [catalog, setCatalog] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [form, setForm] = useState(blankPersona);
  const [mode, setMode] = useState("browse");
  const [activeCategory, setActiveCategory] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const loadData = async () => {
    try {
      const [catalogResponse, personasResponse] = await Promise.all([
        personaApi.catalog(),
        personaApi.all(),
      ]);
      setCatalog(catalogResponse.data);
      setPersonas(personasResponse.data || []);
      setActiveCategory((current) => current || catalogResponse.data?.categories?.[0] || "");
    } catch (error) {
      setToast({ message: error.message || "Error loading personas", tone: "error" });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const prompts = useMemo(() => catalog?.prompts || [], [catalog]);
  const visiblePrompts = useMemo(
    () => prompts.filter((prompt) => prompt.category === activeCategory && (showAdvanced || !prompt.advanced)),
    [activeCategory, prompts, showAdvanced]
  );
  const overrideCount = Object.keys(form.promptOverrides || {}).length;
  const isCorePersona = form.personaId === "core-chara";

  const startCreate = () => {
    setForm({ ...blankPersona, promptOverrides: {} });
    setMode("edit");
  };

  const viewCore = () => {
    setForm({ ...blankPersona, ...catalog.corePersona, promptOverrides: {} });
    setMode("edit");
  };

  const editPersona = (persona) => {
    setForm({ ...blankPersona, ...persona, promptOverrides: { ...(persona.promptOverrides || {}) } });
    setMode("edit");
  };

  const patchForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const patchPhilosophyMap = (distance, value) => setForm((current) => {
    const next = { ...(current.philosophyIdsByDistance || {}) };
    if (value) {
      next[distance] = value;
    } else {
      delete next[distance];
    }
    return { ...current, philosophyIdsByDistance: next };
  });

  const toggleOverride = (prompt) => {
    setForm((current) => {
      const overrides = { ...(current.promptOverrides || {}) };
      if (Object.prototype.hasOwnProperty.call(overrides, prompt.key)) {
        delete overrides[prompt.key];
      } else {
        overrides[prompt.key] = prompt.defaultValue;
      }
      return { ...current, promptOverrides: overrides };
    });
  };

  const updateOverride = (key, value) => {
    setForm((current) => ({
      ...current,
      promptOverrides: { ...(current.promptOverrides || {}), [key]: value },
    }));
  };

  const savePersona = async () => {
    if (!form.coachDisplayName.trim()) {
      setToast({ message: "Coach display name is required", tone: "error" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        personaId: form.personaId || undefined,
        version: form.version || "1",
        coachDisplayName: form.coachDisplayName,
        coachPersonality: form.coachPersonality || undefined,
        imageUrl: form.imageUrl || undefined,
        basePersonaId: form.basePersonaId || "core-chara",
        philosophyIdsByDistance: form.philosophyIdsByDistance || {},
        status: form.status || "ACTIVE",
        promptOverrides: form.promptOverrides || {},
      };
      if (form.personaId) {
        await personaApi.update(form.personaId, form.version || "1", payload);
      } else {
        await personaApi.create(payload);
      }
      await loadData();
      setMode("browse");
      setToast({ message: "Persona saved", tone: "success" });
    } catch (error) {
      setToast({ message: error.response?.data?.message || error.message || "Error saving persona", tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!catalog) {
    return <Card title="Personas"><div className="empty-state">Loading editable coaching prompts...</div><Toast toast={toast} onClose={() => setToast(null)} /></Card>;
  }

  if (mode === "browse") {
    return (
      <section className="persona-panel">
        <Toast toast={toast} onClose={() => setToast(null)} />
        <div className="persona-panel-header">
          <div>
            <div className="section-kicker">Coach identity library</div>
            <h2>Personas</h2>
            <p>Create focused coaching voices without exposing internal prompt plumbing.</p>
          </div>
          <Button onClick={startCreate}>+ New persona</Button>
        </div>
        <div className="persona-card-grid">
          <button className="persona-library-card persona-library-card--core" type="button" onClick={viewCore}>
            <span className="section-kicker">Built in</span>
            <strong>{catalog.corePersona.coachDisplayName}</strong>
            <span>Core coaching baseline</span>
          </button>
          {personas.map((persona) => (
            <button className="persona-library-card" type="button" key={`${persona.personaId}:${persona.version}`} onClick={() => editPersona(persona)}>
              <span className="section-kicker">{persona.status || "ACTIVE"}</span>
              <strong>{persona.coachDisplayName}</strong>
              <span>{persona.personaId} · v{persona.version}</span>
            </button>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="persona-panel">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="persona-panel-header">
        <div>
          <button className="persona-back-link" type="button" onClick={() => setMode("browse")}>← Persona library</button>
          <div className="section-kicker">{isCorePersona ? "Read-only baseline" : form.personaId ? "Edit persona" : "Create persona"}</div>
          <h2>{isCorePersona ? "Core Chara" : form.coachDisplayName || "New coaching persona"}</h2>
          <p>{overrideCount} explicit prompt override{overrideCount === 1 ? "" : "s"}. Unchanged fields inherit the core coaching prompt.</p>
        </div>
        {!isCorePersona && <Button onClick={savePersona} loading={saving}>Save persona</Button>}
      </div>

      <Card title="Coach identity">
        <div className="persona-metadata-grid">
          <Input label="Persona ID" value={form.personaId} onChange={(value) => patchForm("personaId", value)} placeholder="Generated when blank" disabled={Boolean(form.personaId)} />
          <Input label="Version" value={form.version} onChange={(value) => patchForm("version", value)} disabled={isCorePersona} />
          <Input label="Coach display name" value={form.coachDisplayName} onChange={(value) => patchForm("coachDisplayName", value)} disabled={isCorePersona} />
          <Input label="Image URL" value={form.imageUrl || ""} onChange={(value) => patchForm("imageUrl", value)} placeholder="/personas/alder-coach.png or https://..." disabled={isCorePersona} />
          <Select label="Coach personality" value={form.coachPersonality || ""} onChange={(value) => patchForm("coachPersonality", value)} options={personalityOptions} disabled={isCorePersona} />
          <Select label="Status" value={form.status || "ACTIVE"} onChange={(value) => patchForm("status", value)} options={statusOptions} disabled={isCorePersona} />
        </div>
        <div className="persona-distance-map">
          {distanceOptions.map((distance) => (
            <Input
              key={distance}
              label={`${distance.replaceAll("_", " ")} philosophy`}
              value={form.philosophyIdsByDistance?.[distance] || ""}
              onChange={(value) => patchPhilosophyMap(distance, value)}
              disabled={isCorePersona}
            />
          ))}
        </div>
      </Card>

      <div className="persona-prompt-shell">
        <aside className="persona-category-rail">
          <div>
            <span className="section-kicker">Prompt controls</span>
            <h3>Coaching layers</h3>
          </div>
          {catalog.categories.map((category) => (
            <button className={activeCategory === category ? "active" : ""} type="button" key={category} onClick={() => setActiveCategory(category)}>
              {category}
            </button>
          ))}
          <label className="persona-advanced-toggle">
            <input type="checkbox" checked={showAdvanced} onChange={(event) => setShowAdvanced(event.target.checked)} />
            Show advanced prompts
          </label>
        </aside>

        <div className="persona-prompt-list">
          {visiblePrompts.length === 0 && <div className="empty-state">Advanced prompts in this category are hidden. Enable advanced prompts to review them.</div>}
          {visiblePrompts.map((prompt) => {
            const overridden = Object.prototype.hasOwnProperty.call(form.promptOverrides || {}, prompt.key);
            return (
              <article className={`persona-prompt-card ${overridden ? "persona-prompt-card--overridden" : ""}`} key={prompt.key}>
                <div className="persona-prompt-card-header">
                  <div>
                    <div className="persona-prompt-title">{prompt.label}</div>
                    <p>{prompt.description}</p>
                  </div>
                  <div className="persona-prompt-actions">
                    {prompt.advanced && <Pill>Advanced</Pill>}
                    {!isCorePersona && <Button tone="ghost" onClick={() => toggleOverride(prompt)}>{overridden ? "Reset" : "Override"}</Button>}
                  </div>
                </div>
                {prompt.templateTokens?.length > 0 && <div className="persona-token-note">Keep template tokens: {prompt.templateTokens.join(", ")}</div>}
                <TextArea
                  label={overridden ? "Persona override" : "Inherited core prompt"}
                  value={overridden ? form.promptOverrides[prompt.key] : prompt.defaultValue}
                  onChange={(value) => updateOverride(prompt.key, value)}
                  rows={Math.min(12, Math.max(4, Math.ceil((overridden ? form.promptOverrides[prompt.key] : prompt.defaultValue).length / 110)))}
                  disabled={!overridden || isCorePersona}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
